import { useEffect, useState } from 'react';
import { Button } from './button';
import { Dialog } from './dialog';
import { Input } from './input';

export interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  /** Plain-words consequence of confirming (KT §5.4). */
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** When set, the confirm button stays disabled until the user types this exact text (the name of what is archived or deleted). */
  requireText?: string;
  /** Names what to type, e.g. "project name" -> "Type the project name to confirm". */
  requireLabel?: string;
}

/**
 * Short confirmation (Figma P01 "Destructive confirm"). Confirm is a dark button, never red: sensitive != destructive-styled
 * (KT §5.4). Archiving or deleting something asks for its name first, via `requireText`.
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  requireText,
  requireLabel,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const blocked = requireText !== undefined && typed.trim() !== requireText;

  // Whatever closes it (Cancel, Esc, a successful confirm), the next time it opens the box is empty again.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const handleCancel = () => {
    setTyped('');
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      title={title}
      description={description}
      density="confirm"
      footer={
        <>
          <Button variant="secondary" size="dialog" onClick={handleCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button size="dialog" onClick={onConfirm} disabled={blocked} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {requireText !== undefined ? (
        <Input
          variant="filled"
          label={requireLabel ? `Type the ${requireLabel} to confirm` : `Type “${requireText}” to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
        />
      ) : null}
    </Dialog>
  );
}
